import { useMemo, useRef, useState } from 'react';
import { buildProjectContext } from '../bridge/context';
import { commitStaged, stagePackage, type EntityChange, type StageResult, type StagedPackage } from '../bridge/engine';
import { PACKAGE_LIMITS } from '../bridge/package';
import { VisualReviewPanel } from '../components/assistant/VisualReviewPanel';
import { useSceneEdit } from '../components/scene/useSceneEdit';
import { Alert, Button, ConfirmDialog, Panel, inputClass } from '../components/ui';
import { downloadText } from '../lib/download';
import type { WorkspaceTab } from '../prompt/executor';
import { useProject } from '../state/projectContext';

/** Copies tried when the owner imports an already-applied package again. */
const MAX_COPIES = 20;

type Failure = Extract<StageResult, { ok: false }>;

const FAILURE_TITLES: Record<Failure['kind'], string> = {
  size: 'The package is too large.',
  json: 'This is not valid JSON.',
  format: 'This is not a SonicForge creation package.',
  version: 'This package version is not supported, or is too old for the fields it uses.',
  schema: 'The package does not follow the creation package format.',
  compatibility: 'This package was written for a different project.',
  conflict: 'The project changed since the package was written.',
  'already-applied': 'This package has already been applied to this project.',
  operation: 'An operation in the package cannot be applied.',
  validation: 'The result would not be a valid project.',
};

/**
 * Assistant Creation (Phase 5A). The owner pastes or opens a creation package written by an
 * external assistant; nothing runs until they press Validate, and nothing changes until they
 * press Apply. The package is plain data: it is checked against a strict schema, applied to
 * a copy with the ordinary editing operations, and committed as one undoable edit.
 */
export function AssistantWorkspace({ onOpen }: { onOpen: (tab: WorkspaceTab) => void }) {
  const { project, session } = useProject();
  const { edit, error: editError, setError: setEditError } = useSceneEdit();
  const [text, setText] = useState('');
  const [result, setResult] = useState<StageResult | null>(null);
  const [copyOffer, setCopyOffer] = useState<number | null>(null);
  const [confirmDestructive, setConfirmDestructive] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'info'; title: string; body?: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lockLost = session.editingLock === 'lost';

  const contextText = useMemo(() => JSON.stringify(buildProjectContext(project), null, 2), [project]);

  const reset = () => {
    setResult(null);
    setCopyOffer(null);
  };

  const validate = (copy = 0) => {
    setNotice(null);
    const r = stagePackage(project, text, { copy });
    setResult(r);
    setCopyOffer(null);
    if (!r.ok && r.kind === 'already-applied') {
      for (let n = 1; n <= MAX_COPIES; n++) {
        const attempt = stagePackage(project, text, { copy: n });
        if (attempt.ok || attempt.kind !== 'already-applied') {
          setCopyOffer(n);
          break;
        }
      }
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    reset();
    setNotice(null);
    if (file.size > PACKAGE_LIMITS.bytes) {
      setNotice({ tone: 'error', title: `“${file.name}” is ${Math.ceil(file.size / 1024)} KiB; packages are limited to ${PACKAGE_LIMITS.bytes / 1024} KiB.` });
      return;
    }
    setText(await file.text());
    setNotice({ tone: 'info', title: `Loaded “${file.name}”. Press Validate to check it; nothing has been applied.` });
  };

  const apply = (staged: StagedPackage) => {
    setConfirmDestructive(false);
    // One transaction: commitStaged runs on the project the edit is applied to, so the
    // check and the write cannot be separated. If the scenes, characters, animations or
    // sync sessions changed after the preview, it throws: nothing is written, no undo step
    // is recorded and nothing is saved. Unrelated changes (music, save metadata) are kept.
    let stale = false;
    const applied = edit(`Assistant package: ${staged.pkg.title}`, (current) => {
      const next = commitStaged(current, staged);
      if (!next) {
        stale = true;
        throw new Error('The project changed after the preview.');
      }
      return next;
    });
    if (stale) {
      setEditError(null); // shown below as the stale-preview notice instead
      setResult(null);
      setNotice({ tone: 'error', title: 'The project changed after the preview.', body: 'Nothing was applied. Press Validate again to preview the package against the current project.' });
      return;
    }
    if (applied) {
      setResult(null);
      setNotice({ tone: 'success', title: `Applied “${staged.pkg.title}”.`, body: 'It is ordinary project content now: edit it in the Animation, Sync and Visualizer workspaces, or undo it there as one step.' });
    }
  };

  const onApply = (staged: StagedPackage) => {
    if (staged.destructive.length > 0) setConfirmDestructive(true);
    else apply(staged);
  };

  const staged = result?.ok ? result.staged : null;
  const failure = result && !result.ok ? result : null;

  return (
    <div className="flex flex-col gap-4" data-testid="assistant-workspace">
      <Alert tone="info" title="No AI model runs inside SonicForge.">
        <p>
          Ask ChatGPT, Claude or another assistant to write a <em>creation package</em> (a JSON file) using the format in{' '}
          <code>docs/ASSISTANT_CREATION_PACKAGES.md</code>, then paste or open it here. SonicForge checks it locally,
          shows you what it would change, and changes nothing until you press Apply. Packages are data only: they cannot
          run code, load web addresses or files, or replace your project.
        </p>
      </Alert>

      <Panel
        title="Creation package"
        actions={
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              data-testid="package-file"
              onChange={(e) => {
                void onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button size="sm" onClick={() => fileRef.current?.click()}>
              Open file…
            </Button>
          </>
        }
      >
        <label htmlFor="package-text" className="sr-only">
          Creation package JSON
        </label>
        <textarea
          id="package-text"
          data-testid="package-text"
          className={`${inputClass} min-h-48 font-mono text-xs`}
          spellCheck={false}
          placeholder='{"format": "sonicforge-creation-package", "version": 2, …}'
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            reset();
          }}
        />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={() => validate()} disabled={text.trim().length === 0} data-testid="package-validate">
            Validate and preview
          </Button>
          <Button
            onClick={() => {
              setText('');
              reset();
              setNotice(null);
            }}
            disabled={text.length === 0 && !result}
          >
            Clear
          </Button>
          <span className="text-xs text-zinc-400">
            Up to {PACKAGE_LIMITS.bytes / 1024} KiB and {PACKAGE_LIMITS.operations} operations.
          </span>
        </div>
      </Panel>

      {notice && (
        <div data-testid="package-notice" data-tone={notice.tone}>
          <Alert tone={notice.tone} title={notice.title}>
            {notice.body}
          </Alert>
        </div>
      )}
      {editError && <Alert title={editError} />}

      {failure && (
        <div data-testid="package-errors" data-kind={failure.kind}>
          <Alert
            title={FAILURE_TITLES[failure.kind]}
            action={
              copyOffer !== null ? (
                <Button size="sm" onClick={() => validate(copyOffer)} data-testid="package-copy">
                  Preview as copy {copyOffer}
                </Button>
              ) : undefined
            }
          >
            <p>Nothing was applied.</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {failure.errors.map((e, i) => (
                <li key={i}>
                  {e.path && <code className="mr-1 text-red-200">{e.path}</code>}
                  {e.message}
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      )}

      {staged && (
        <Panel title="Preview" className="border-accent-700/60">
          <div data-testid="package-preview" className="grid gap-4 text-sm">
            <div>
              <p className="font-semibold text-zinc-100" data-testid="package-title">
                {staged.pkg.title}
                {staged.copy > 0 && <span className="ml-2 text-xs text-zinc-400">(copy {staged.copy})</span>}
              </p>
              {staged.pkg.author && <p className="text-xs text-zinc-400">By {staged.pkg.author}</p>}
              {staged.pkg.explanation && (
                <p className="mt-2 whitespace-pre-wrap text-zinc-300" data-testid="package-explanation">
                  {staged.pkg.explanation}
                </p>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-2 text-xs text-zinc-400 sm:grid-cols-4">
              <Stat label="Operations" value={staged.operations.length} />
              <Stat label="Created" value={staged.created.length} testId="count-created" />
              <Stat label="Changed" value={staged.modified.length} testId="count-modified" />
              <Stat label="Removed" value={staged.deleted.length} testId="count-deleted" />
            </dl>
            {staged.keyframesAdded > 0 && <p className="text-xs text-zinc-400">Adds {staged.keyframesAdded} keyframes.</p>}
            {staged.warnings.length > 0 && (
              <div data-testid="package-warnings">
                <Alert tone="warning" title="Check before applying">
                  <ul className="list-disc space-y-1 pl-5">
                    {staged.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </Alert>
              </div>
            )}
            {staged.destructive.length > 0 && (
              <div data-testid="package-destructive">
                <Alert title="This package removes or replaces existing content">
                  <ul className="list-disc space-y-1 pl-5">
                    {staged.destructive.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                </Alert>
              </div>
            )}
            <ChangeList title="Created" items={staged.created} />
            <ChangeList title="Changed" items={staged.modified} />
            <ChangeList title="Removed" items={staged.deleted} />
            <details>
              <summary className="cursor-pointer text-xs text-zinc-400">Operations ({staged.operations.length})</summary>
              <ol className="mt-2 list-decimal space-y-0.5 pl-6 text-xs text-zinc-300" data-testid="package-operations">
                {staged.operations.map((o) => (
                  <li key={o.index}>
                    <code className="text-zinc-400">{o.op}</code> {o.description}
                  </li>
                ))}
              </ol>
            </details>
            {lockLost && <Alert title="This project is open in another tab, so changes here can’t be saved. Close the other tab and reopen the project before applying." />}
            <div className="flex flex-wrap gap-2">
              <Button variant={staged.destructive.length ? 'danger' : 'primary'} onClick={() => onApply(staged)} disabled={lockLost} data-testid="package-apply">
                Apply to project
              </Button>
              <Button onClick={() => setResult(null)} data-testid="package-cancel">
                Cancel
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {notice?.tone === 'success' && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onOpen('animation')} data-testid="open-animation">
            Open in Animation
          </Button>
          <Button onClick={() => onOpen('sync')}>Open in Sync</Button>
        </div>
      )}

      <Panel
        title="Project Context for the assistant"
        actions={
          <>
            <Button
              size="sm"
              onClick={() => {
                if (!navigator.clipboard) {
                  setNotice({ tone: 'error', title: 'This browser does not allow copying here. Use Download instead.' });
                  return;
                }
                void navigator.clipboard.writeText(contextText).then(
                  () => setNotice({ tone: 'info', title: 'Project Context copied. Paste it into your assistant together with the authoring guide.' }),
                  () => setNotice({ tone: 'error', title: 'The browser did not allow copying. Use Download instead.' }),
                );
              }}
              data-testid="context-copy"
            >
              Copy
            </Button>
            <Button size="sm" onClick={() => downloadText('sonicforge-project-context.json', contextText)} data-testid="context-download">
              Download
            </Button>
          </>
        }
      >
        <p className="text-xs text-zinc-400">
          Give this to the assistant when you want a package that changes this project. It lists the ids, names, sizes
          and timing of your scenes, characters, animations and sync sessions, plus the operations a package may use. It
          does not include your project name or description, music, recordings or any file contents.
        </p>
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-zinc-400">Show ({Math.ceil(contextText.length / 1024)} KiB)</summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded bg-zinc-950 p-2 text-[11px] text-zinc-300" data-testid="context-text">
            {contextText}
          </pre>
        </details>
      </Panel>

      <VisualReviewPanel project={project} />

      <ConfirmDialog
        open={confirmDestructive && !!staged}
        title="Apply a package that removes content?"
        danger
        body={
          <>
            <p>Applying this package will:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">{staged?.destructive.map((d) => <li key={d}>{d}</li>)}</ul>
            <p className="mt-2">You can undo it afterwards as one step.</p>
          </>
        }
        confirmLabel="Remove and apply"
        onConfirm={() => staged && apply(staged)}
        onCancel={() => setConfirmDestructive(false)}
      />
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: number; testId?: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="text-zinc-200" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function ChangeList({ title, items }: { title: string; items: EntityChange[] }) {
  if (items.length === 0) return null;
  return (
    <details>
      <summary className="cursor-pointer text-xs text-zinc-400">
        {title} ({items.length})
      </summary>
      <ul className="mt-2 grid gap-0.5 text-xs text-zinc-300 sm:grid-cols-2">
        {items.map((c) => (
          <li key={`${c.kind}-${c.id}`}>
            <span className="text-zinc-400">{c.kind}</span> {c.name}
            {c.sceneName && <span className="text-zinc-400"> · {c.sceneName}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}
